import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import Pagination from "@/components/feature/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  allMovies,
  categories,
  filterMovies,
  getCategory,
} from "@/lib/movies";
import type { SortValue } from "@/lib/movies";
import FilterBar from "@/pages/category/components/FilterBar";

const PAGE_SIZE = 20;

interface FilterValues {
  tag: string;
  year: string;
  minRating: string;
  sort: SortValue;
}

const defaultFilters: FilterValues = {
  tag: "all",
  year: "all",
  minRating: "all",
  sort: "rating",
};

export default function CategoryPage() {
  const { type } = useParams();
  const category = getCategory(type);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterValues>(defaultFilters);
  const [sheetOpen, setSheetOpen] = useState(false);

  const pageParam = Number(searchParams.get("page") ?? "1");
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  useEffect(() => {
    setFilters(defaultFilters);
    setSheetOpen(false);
  }, [type]);

  const pool = useMemo(
    () => (category.slug === "all" ? allMovies : allMovies.filter((movie) => movie.type === category.slug)),
    [category.slug]
  );

  const tags = useMemo(() => {
    const counter = new Map<string, number>();
    pool.forEach((movie) => {
      movie.tags.forEach((tag) => counter.set(tag, (counter.get(tag) ?? 0) + 1));
    });
    return [...counter.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map((entry) => entry[0]);
  }, [pool]);

  const results = useMemo(() => {
    const filtered = filterMovies(pool, filters);
    if (filters.tag === "all") return filtered;
    return filtered.filter((movie) => movie.tags.includes(filters.tag));
  }, [pool, filters]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const averageRating =
    results.length > 0
      ? (results.reduce((sum, movie) => sum + movie.rating, 0) / results.length).toFixed(1)
      : "—";

  usePageMeta({
    title: `${category.name} - 韩国伦理电影在线观看`,
    description: category.description,
    keywords: `${category.name},韩国伦理电影,伦理电影在线观看,${category.name}推荐`,
  });

  const updateFilters = (patch: Partial<FilterValues>) => {
    setFilters((current) => ({ ...current, ...patch }));
    if (searchParams.get("page")) {
      setSearchParams({});
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSearchParams({});
  };

  const changePage = (next: number) => {
    const target = Math.min(Math.max(next, 1), totalPages);
    if (target === 1) {
      setSearchParams({});
    } else {
      setSearchParams({ page: String(target) });
    }
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const filterBar = (
    <FilterBar values={filters} tags={tags} onChange={updateFilters} onReset={resetFilters} />
  );

  return (
    <Layout>
      <div className="bg-background-100/60 border-b border-background-200/70">
        <div className="px-4 md:px-8 lg:px-12 pt-8 pb-8 md:pt-10 md:pb-10">
          <div className="max-w-shell mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                ...(category.slug === "all"
                  ? []
                  : [{ label: "分类片库", to: "/category/all" }]),
                { label: category.name },
              ]}
            />
            <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div className="max-w-2xl">
                <p className="font-label text-xs tracking-[0.32em] text-primary-500 mb-2">
                  {category.en} · CATEGORY
                </p>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">
                  {category.name}
                </h1>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{category.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5">
                  <p className="font-label text-xl text-primary-500 leading-none">{results.length}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">部影片</p>
                </div>
                <div className="rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5">
                  <p className="font-label text-xl text-accent-500 leading-none">{averageRating}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">平均评分</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10">
        <div className="max-w-shell mx-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="md:hidden h-10 px-4 rounded-md border border-background-300 text-foreground-800 text-sm flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-equalizer-line" aria-hidden="true"></i>
              筛选
              {(filters.tag !== "all" || filters.year !== "all" || filters.minRating !== "all") && (
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              )}
            </button>
            <p className="text-xs text-foreground-500 hidden md:block">
              共 {results.length} 部影片 · 每页 {PAGE_SIZE} 部 · 第 {currentPage} / {totalPages} 页
            </p>
            <p className="text-xs text-foreground-500 md:hidden">
              {results.length} 部 · {currentPage}/{totalPages} 页
            </p>
          </div>

          <div className="hidden md:block mb-6">{filterBar}</div>

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} compact />
              ))}
            </div>
          ) : (
            <EmptyState
              title="暂无符合条件的影片"
              description="当前筛选条件下没有找到影片，试试放宽年份或评分范围，或者重置全部筛选条件。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}

          <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <h2 className="text-sm font-medium text-foreground-900 mb-4">浏览其他分类</h2>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((item) => item.slug !== category.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className="h-9 px-4 rounded-md border border-background-200 text-sm text-foreground-700 flex items-center gap-1.5 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {item.name}
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>

      {sheetOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm cursor-pointer"
          ></button>
          <div className="absolute bottom-0 left-0 right-0 rounded-t-xl border-t border-background-200 bg-background-50 animate-fade-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-background-200/70">
              <h2 className="text-sm font-medium text-foreground-950">筛选影片</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-600 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <div className="p-4">{filterBar}</div>
            <div className="px-4 pb-5 pt-1">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看 {results.length} 部影片
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}